import { useCallback, useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/use-auth";
import type { ParsedTrack } from "@/lib/parseTrack";

export type SessionVisibility = "public" | "private";

export type SurfSession = {
  id: string;
  user_id: string;
  source: "manual" | "upload";
  title: string;
  spot_name: string | null;
  lat: number | null;
  lon: number | null;
  started_at: string;
  duration_minutes: number | null;
  distance_km: number | null;
  avg_speed_kmh: number | null;
  max_speed_kmh: number | null;
  notes: string | null;
  visibility: SessionVisibility;
  created_at: string;
};

export type FeedSession = SurfSession & {
  author_name: string;
  author_avatar: string | null;
};

export function useSessions() {
  const { user } = useAuth();
  const [mine, setMine] = useState<SurfSession[]>([]);
  const [feed, setFeed] = useState<FeedSession[]>([]);
  const [loading, setLoading] = useState(true);

  const loadMine = useCallback(async () => {
    if (!user) { setMine([]); return; }
    const { data, error } = await supabase
      .from("surf_sessions")
      .select("*")
      .eq("user_id", user.id)
      .order("started_at", { ascending: false });
    if (!error && data) setMine(data as SurfSession[]);
  }, [user]);

  const loadFeed = useCallback(async () => {
    if (!user) { setFeed([]); return; }

    // surf_sessions.user_id references auth.users (not public.profiles), so
    // there's no FK PostgREST can embed on — fetch sessions and profiles
    // separately and join them client-side.
    const { data: publicSessions } = await supabase
      .from("surf_sessions")
      .select("*")
      .eq("visibility", "public")
      .order("started_at", { ascending: false })
      .limit(50);

    if (!publicSessions) { setFeed([]); return; }

    const userIds = [...new Set(publicSessions.map((s) => s.user_id))];
    const { data: profiles } = await supabase
      .from("profiles")
      .select("id, display_name, avatar_url")
      .in("id", userIds.length ? userIds : ["00000000-0000-0000-0000-000000000000"]);

    const byId = new Map((profiles ?? []).map((p) => [p.id, p]));
    setFeed(
      publicSessions.map((s) => ({
        ...(s as SurfSession),
        author_name: byId.get(s.user_id)?.display_name ?? "A surfer",
        author_avatar: byId.get(s.user_id)?.avatar_url ?? null,
      })),
    );
  }, [user]);

  const refresh = useCallback(async () => {
    setLoading(true);
    await Promise.all([loadMine(), loadFeed()]);
    setLoading(false);
  }, [loadMine, loadFeed]);

  useEffect(() => { refresh(); }, [refresh]);

  /** Manual entry — no track data, just what the user typed in. */
  const logSession = useCallback(async (input: {
    title: string;
    spot_name: string | null;
    lat: number | null;
    lon: number | null;
    started_at: string;
    duration_minutes: number | null;
    notes: string | null;
    visibility: SessionVisibility;
  }) => {
    if (!user) throw new Error("Not signed in");
    const { error } = await supabase.from("surf_sessions").insert({
      user_id: user.id,
      source: "manual",
      ...input,
    });
    if (error) throw error;
    await refresh();
  }, [user, refresh]);

  /** From a parsed GPX/TCX file — distance/speed/track come from the file itself. */
  const logUploadedSession = useCallback(async (input: {
    title: string;
    spot_name: string | null;
    notes: string | null;
    visibility: SessionVisibility;
    track: ParsedTrack;
  }) => {
    if (!user) throw new Error("Not signed in");
    const { error } = await supabase.from("surf_sessions").insert({
      user_id: user.id,
      source: "upload",
      title: input.title,
      spot_name: input.spot_name,
      notes: input.notes,
      visibility: input.visibility,
      started_at: input.track.startedAt,
      duration_minutes: input.track.durationMinutes,
      distance_km: input.track.distanceKm,
      avg_speed_kmh: input.track.avgSpeedKmh,
      max_speed_kmh: input.track.maxSpeedKmh,
      lat: input.track.lat,
      lon: input.track.lon,
      polyline: JSON.stringify(input.track.points),
    });
    if (error) throw error;
    await refresh();
  }, [user, refresh]);

  const setVisibility = useCallback(async (id: string, visibility: SessionVisibility) => {
    const { error } = await supabase.from("surf_sessions").update({ visibility }).eq("id", id);
    if (error) throw error;
    await refresh();
  }, [refresh]);

  const deleteSession = useCallback(async (id: string) => {
    const { error } = await supabase.from("surf_sessions").delete().eq("id", id);
    if (error) throw error;
    await refresh();
  }, [refresh]);

  return { mine, feed, loading, refresh, logSession, logUploadedSession, setVisibility, deleteSession };
}
